import { supabase } from './supabase';

export interface ChecklistItem {
  key: string;
  label: string;
  description?: string;
  completed: boolean;
  completedAt?: string;
  actionLabel?: string;
  actionType?: 'navigate' | 'done' | 'external';
  actionTarget?: string;
}

export interface CreatorStats {
  totalEarnings: number;
  pendingApprovalEarnings: number; // Shown to the creator but gated on admin review (e.g. bio-link bonus)
  totalViews: number;
  avgCtr: number;
}

export interface AdminSuggestion {
  id: string;
  title: string;
  body: string;
  created_at: string;
  dismissed_at: string | null;
}

export async function fetchChecklistProgress(talentId: string): Promise<Record<string, { completed_at: string; metadata: any }>> {
  const { data } = await supabase
    .from('creator_checklist_progress')
    .select('item_key, completed_at, metadata')
    .eq('talent_id', talentId);

  const map: Record<string, { completed_at: string; metadata: any }> = {};
  (data ?? []).forEach(row => {
    if (row.completed_at) {
      map[row.item_key] = { completed_at: row.completed_at, metadata: row.metadata };
    }
  });
  return map;
}

export async function markChecklistItem(talentId: string, itemKey: string, metadata: any = {}) {
  const { error } = await supabase
    .from('creator_checklist_progress')
    .upsert({
      talent_id: talentId,
      item_key: itemKey,
      completed_at: new Date().toISOString(),
      metadata,
    }, { onConflict: 'talent_id,item_key' });

  if (error) throw error;
}

export async function fetchCreatorStats(talentId: string): Promise<CreatorStats> {
  const [earningsRes, viewsRes, clicksRes, sponsorEarningsRes, bioProofsRes, bioBonusPayoutRes] = await Promise.all([
    supabase
      .from('payouts')
      .select('payout_amount, payout_type')
      .eq('talent_id', talentId)
      .eq('is_refunded', false),
    supabase
      .from('bio_page_views')
      .select('id', { count: 'exact', head: true })
      .eq('talent_id', talentId),
    supabase
      .from('bio_link_clicks')
      .select('id', { count: 'exact', head: true })
      .eq('talent_id', talentId),
    supabase
      .from('sponsor_clicks')
      .select('creator_share_cents')
      .eq('talent_id', talentId),
    supabase
      .from('creator_bio_proofs')
      .select('id', { count: 'exact', head: true })
      .eq('talent_id', talentId),
    supabase
      .from('payouts')
      .select('id', { count: 'exact', head: true })
      .eq('talent_id', talentId)
      .eq('payout_type', 'Checklist: Bio Link Bonus'),
  ]);

  const orderEarnings = (earningsRes.data ?? []).reduce(
    (sum, p) => sum + Number(p.payout_amount || 0), 0
  );

  const sponsorEarnings = (sponsorEarningsRes.data ?? []).reduce(
    (sum, s) => sum + Number(s.creator_share_cents || 0), 0
  ) / 100;

  // Bio-link $50 bonus is awarded via a DB trigger when an admin approves the
  // creator's 2 bio-proof screenshots. Before approval there is no payouts
  // row — but the creator has done their part of the work, so surface the
  // pending $50 in their total so the Total Earnings card isn't confusing
  // (this matched the $100 "expected" total the creator sees after finishing
  // the checklist: $20 chat + $30 course + $50 bio link).
  const proofCount = bioProofsRes.count ?? 0;
  const bioBonusPayoutExists = (bioBonusPayoutRes.count ?? 0) > 0;
  const pendingApprovalEarnings = proofCount >= 2 && !bioBonusPayoutExists ? 50 : 0;

  const totalEarnings = orderEarnings + sponsorEarnings + pendingApprovalEarnings;

  const totalViews = viewsRes.count ?? 0;
  const totalClicks = clicksRes.count ?? 0;
  const avgCtr = totalViews > 0 ? (totalClicks / totalViews) * 100 : 0;

  return { totalEarnings, pendingApprovalEarnings, totalViews, avgCtr };
}

export async function fetchAdminSuggestions(talentId: string): Promise<AdminSuggestion[]> {
  const { data } = await supabase
    .from('admin_suggestions')
    .select('id, title, body, created_at, dismissed_at')
    .eq('talent_id', talentId)
    .is('dismissed_at', null)
    .order('created_at', { ascending: false })
    .limit(10);

  return data ?? [];
}

export async function fetchBioProofCount(talentId: string): Promise<number> {
  const { count } = await supabase
    .from('creator_bio_proofs')
    .select('id', { count: 'exact', head: true })
    .eq('talent_id', talentId);

  return count ?? 0;
}

export async function uploadBioProof(talentId: string, file: File, platform: string): Promise<string> {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-50);
  const filePath = `bio-proofs/${talentId}/${Date.now()}_${safeName}`;
  const { error: uploadError } = await supabase.storage
    .from('platform-assets')
    .upload(filePath, file, { contentType: file.type });

  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage
    .from('platform-assets')
    .getPublicUrl(filePath);

  const screenshotUrl = urlData.publicUrl;

  const { error: insertError } = await supabase
    .from('creator_bio_proofs')
    .insert({
      talent_id: talentId,
      screenshot_url: screenshotUrl,
      platform,
    });

  if (insertError) throw insertError;
  return screenshotUrl;
}

export async function hasCustomizedLink(talentId: string): Promise<boolean> {
  const { data: bioLinks } = await supabase
    .from('bio_links')
    .select('id')
    .eq('talent_id', talentId);

  if (!bioLinks || bioLinks.length === 0) return false;
  const { count: cardsCount } = await supabase
    .from('bio_grid_cards')
    .select('id', { count: 'exact', head: true })
    .in('bio_link_id', bioLinks.map(b => b.id));

  return (cardsCount ?? 0) > 0 || bioLinks.length > 0;
}

export async function hasSponsorEnabled(talentId: string): Promise<boolean> {
  const { count } = await supabase
    .from('sponsor_creator_assignments')
    .select('id', { count: 'exact', head: true })
    .eq('talent_id', talentId)
    .eq('is_active', true);

  return (count ?? 0) > 0;
}

export async function fetchCourseProgress(talentId: string): Promise<Record<number, string>> {
  const { data } = await supabase
    .from('creator_course_responses')
    .select('question_number, answer')
    .eq('talent_id', talentId);

  const map: Record<number, string> = {};
  (data ?? []).forEach(row => {
    map[row.question_number] = row.answer;
  });
  return map;
}

export async function saveCourseResponse(talentId: string, questionNumber: number, answer: string) {
  // Use SECURITY DEFINER RPC — more reliable than the RLS-gated upsert, which
  // can silently fail when the bioapp's magic-link session hasn't hydrated.
  const { error } = await supabase.rpc('save_course_response', {
    p_talent_id: talentId,
    p_question_number: questionNumber,
    p_answer: answer,
  });

  if (error) {
    console.error('saveCourseResponse error:', error);
    // Fallback to direct upsert in case the RPC isn't deployed yet
    const { error: fallbackError } = await supabase
      .from('creator_course_responses')
      .upsert({
        talent_id: talentId,
        question_number: questionNumber,
        answer,
      }, { onConflict: 'talent_id,question_number' });
    if (fallbackError) {
      console.error('saveCourseResponse fallback error:', fallbackError);
      throw fallbackError;
    }
  }
}

export async function fetchRecentViewCount(talentId: string, days: number = 30): Promise<number> {
  const since = new Date();
  since.setDate(since.getDate() - days);
  const { count } = await supabase
    .from('bio_page_views')
    .select('id', { count: 'exact', head: true })
    .eq('talent_id', talentId)
    .gte('viewed_at', since.toISOString());
  return count ?? 0;
}

export async function hasSentChatMessage(talentId: string): Promise<boolean> {
  const { data: creatorsChannel } = await supabase
    .from('chat_channels')
    .select('id')
    .eq('channel_type', 'general')
    .ilike('name', 'creators')
    .single();

  if (!creatorsChannel) return false;

  const { count } = await supabase
    .from('chat_messages')
    .select('id', { count: 'exact', head: true })
    .eq('channel_id', creatorsChannel.id)
    .eq('sender_talent_id', talentId);

  return (count ?? 0) > 0;
}
